export type ScribeLayoutReminderOption = 'tomorrow' | 'three_days' | 'one_week';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME_PREFIX = /^(\d{4})-(\d{2})-(\d{2})T/;

const isRealCalendarDate = (year: number, month: number, day: number): boolean => {
  const utc = new Date(Date.UTC(year, month - 1, day));
  return (
    utc.getUTCFullYear() === year &&
    utc.getUTCMonth() === month - 1 &&
    utc.getUTCDate() === day
  );
};

export const getScribeLayoutSwitchAtFallback = (): number =>
  new Date(2026, 10, 15).getTime();

export const resolveScribeLayoutSwitchAt = (raw: string | null | undefined): number => {
  const value = raw?.trim();
  if (!value) return getScribeLayoutSwitchAtFallback();

  const dateOnly = DATE_ONLY.exec(value);
  if (dateOnly) {
    const [, yearValue, monthValue, dayValue] = dateOnly;
    if (!yearValue || !monthValue || !dayValue) return getScribeLayoutSwitchAtFallback();

    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    const local = new Date(year, month - 1, day);
    const isRealDate =
      isRealCalendarDate(year, month, day) &&
      local.getFullYear() === year &&
      local.getMonth() === month - 1 &&
      local.getDate() === day;
    return isRealDate ? local.getTime() : getScribeLayoutSwitchAtFallback();
  }

  const isoDateTime = ISO_DATE_TIME_PREFIX.exec(value);
  if (isoDateTime) {
    const [, yearValue, monthValue, dayValue] = isoDateTime;
    if (!yearValue || !monthValue || !dayValue) return getScribeLayoutSwitchAtFallback();
    if (!isRealCalendarDate(Number(yearValue), Number(monthValue), Number(dayValue))) {
      return getScribeLayoutSwitchAtFallback();
    }
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : getScribeLayoutSwitchAtFallback();
};

const DAY_MS = 24 * 60 * 60 * 1000;

const REMINDER_OPTIONS: readonly ScribeLayoutReminderOption[] = [
  'tomorrow',
  'three_days',
  'one_week',
];

const REMINDER_OPTION_OFFSETS: Record<ScribeLayoutReminderOption, number> = {
  tomorrow: DAY_MS,
  three_days: 3 * DAY_MS,
  one_week: 7 * DAY_MS,
};

export const getScribeLayoutReminderCap = (switchAt: number): number => switchAt - DAY_MS;

export const getScribeLayoutReminderOptions = (
  now: number,
  switchAt: number
): ScribeLayoutReminderOption[] => {
  const cap = getScribeLayoutReminderCap(switchAt);
  return REMINDER_OPTIONS.filter(
    (option) => now + REMINDER_OPTION_OFFSETS[option] <= cap
  );
};

export const getScribeLayoutReminderTarget = (
  option: ScribeLayoutReminderOption,
  now: number
): number => now + REMINDER_OPTION_OFFSETS[option];

export const getScribeLayoutDismissTarget = (now: number, switchAt: number): number => {
  const cap = getScribeLayoutReminderCap(switchAt);
  return cap > now ? Math.min(now + 7 * DAY_MS, cap) : switchAt;
};
